import * as api from '@gitbook/api-client';
import { mapAdvancedCustomizationsToReadableLabels, SITE_FEATURES_MAP } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import { Alert } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { PlanCardRadioFeaturesList, type PlanFeature } from '../../../Components';
import type { SiteCapability } from '../../../state';

/**
 * An alert shown on top of Basic sites using paid features, to invite users to check and possibly upgrade
 */
export function BasicSiteUsingPaidFeaturesAlert(props: {
    site: api.Site;
    sitePermissions: SiteCapability;
}) {
    const { site, sitePermissions } = props;

    if (!sitePermissions.canAdminSite || site.type !== api.SiteType.Basic) {
        return null;
    }

    const frozenFeatures = site.features.filter((feature) => feature.frozen);

    if (frozenFeatures.length === 0) {
        return;
    }

    return (
        <Alert
            kind="danger"
            title="Site downgraded to Basic"
            details={
                <div className="flex flex-col gap-4">
                    <p>
                        This site has been downgraded to Basic, locking all paid feature settings.
                        Upgrade to regain access to configuring:
                    </p>
                    <PlanCardRadioFeaturesList
                        features={frozenFeatures
                            .map((frozenFeature) => {
                                const siteFeatureDefinition = SITE_FEATURES_MAP[frozenFeature.id];
                                if (!siteFeatureDefinition) {
                                    return;
                                }

                                const planFeature: PlanFeature = {
                                    key: frozenFeature.id,
                                    title: siteFeatureDefinition.title,
                                };

                                if (
                                    typeof frozenFeature.customizations === 'undefined' ||
                                    frozenFeature.customizations.length === 0
                                ) {
                                    return planFeature;
                                }

                                return {
                                    ...planFeature,
                                    tooltip: {
                                        label: mapAdvancedCustomizationsToReadableLabels(
                                            frozenFeature.customizations
                                        ).join(', '),
                                    },
                                };
                            })
                            .filter(filterOutNullable)}
                        columns={3}
                        featureListIcon={{
                            icon: Icon.X,
                            props: {
                                className: 'shrink-0 text-muted',
                            },
                        }}
                    />
                </div>
            }
            action={{
                label: 'Upgrade plan',
                href: {
                    current: {
                        tab: {
                            type: 'settings',
                            section: 'plan',
                        },
                    },
                },
            }}
        />
    );
}
